import { css, cx } from '@emotion/css';
import format from 'date-fns/format';
import { graphql } from 'gatsby';
import React, { ReactElement } from 'react';
import { Helmet } from 'react-helmet';
import { FaTwitter } from 'react-icons/all';
import Layout from '../components/Layout';
import TagList from '../components/TagList';
import flex from '../styles/flex';
import { textStyles } from '../styles/utils';
import { colours } from '../styles/variables';
import { SiteMetadata } from '../types/site';

const twitterStyle = css`
  align-items: center;
  border: 1px solid ${colours.primary};
  border-radius: 4px;
  color: ${colours.primary};
  display: flex;
  padding: 0.3rem 0.8rem;
  text-decoration: none;
  transition: background-color 0.5s, color 0.5s ease-in-out;

  &:hover,
  &:active {
    background: ${colours.primary};
    color: ${colours.white};
  }

  span {
    margin-left: 5px;
  }
`;

interface Props {
  data: {
    markdownRemark: {
      html: string;
      timeToRead: number;
      fields: {
        slug: string;
      };
      frontmatter: {
        date: string;
        description: string;
        title: string;
        tags: string[];
      };
    };
    site: {
      siteMetadata: Pick<SiteMetadata, 'siteUrl'>;
    };
  };
}

const PostPage = ({ data }: Props): ReactElement => {
  const {
    markdownRemark: post,
    site: { siteMetadata },
  } = data;
  const {
    frontmatter: { description, title },
    fields: { slug },
    timeToRead,
  } = post;

  return (
    <Layout>
      <div>
        <Helmet title={title}>
          <meta name="description" content={description} />

          <meta name="og:type" content="article" />
          <meta name="og:title" content={title} />
          <meta name="og:description" content={description} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
        </Helmet>
        <h1 className={textStyles.center}>{title}</h1>
        <p className={textStyles.center}>
          <time>{format(new Date(post.frontmatter.date), 'do MMMM yyyy')}</time>
          {` • ${timeToRead} min read`}
        </p>

        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: post.html,
          }}
        />
        <hr />

        <div
          className={cx(
            flex.display,
            flex.alignItems.center,
            flex.justifyContent.between,
          )}
        >
          <a
            className={twitterStyle}
            href={`https://twitter.com/intent/tweet?url=${siteMetadata.siteUrl}${slug}&text=${title}`}
          >
            <FaTwitter />
            <span>Share on Twitter</span>
          </a>

          <TagList tags={post.frontmatter.tags} />
        </div>
      </div>
    </Layout>
  );
};

export default PostPage;

export const query = graphql`
  query BlogPostBySlug($slug: String!) {
    markdownRemark(fields: { slug: { eq: $slug } }) {
      html
      timeToRead
      fields {
        slug
      }
      frontmatter {
        date
        description
        title
        tags
      }
    }
    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`;
